import {
    Box,
    Dialog,
    Typography,
    IconButton,
    styled,
    useTheme,
} from '@mui/material'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'
import moment from 'moment'
import MasterLockOverlay from 'packages/eid-ui/MasterLockOverlay'
import ComputerConnectBtn from 'components/DynamicComponents/ComputerConnectButton'
import { useUrlGenerator } from 'packages/core'
import { useAllApplications } from 'hooks'
import ComputersCheckInButton from 'components/DynamicComponents/ComputersCheckInButton'
import { MOBILE_SCREEN_WIDTH } from 'packages/core/utils/constants'

const mobileScreenWidth = '767px'

const StyledBodyRightInnerResult = styled(Box)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 500,
    color: theme?.palette?.common?.black,
    margin: 0,
}))

const StyledTabConnectButton = styled(Box)({
    '& > div div': {
        height: 'auto !important',
    },
    '& button': {
        width: '200px',
        height: '46px',
        '& span span div': {
            width: '45px',
            display: 'flex',
            justifyContent: 'center',
            height: '100% !important',
        },
        '& div p': {
            fontSize: '16px !important',
        },
    },
})
const StyledSectionHeader = styled(Box)(({ theme }) => ({
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    color: `${theme?.palette?.common?.black} !important`,
    overflow: 'hidden',
    lineHeight: '15px',
    fontSize: '14px',
    padding: '15px 31px',
    borderTop: '1px solid rgba(0, 0, 0, .05)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    fontWeight: 'bold',
    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
    backgroundImage:
        'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
    backgroundSize: '8.00px 8.00px',
    '& svg': {
        margin: '0px !important',
    },
}))
const StyledBodyInner = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginBottom: '16px',
    [`@media (max-width:${MOBILE_SCREEN_WIDTH - 1}px)`]: {
        display: 'block',
    },
})
const StyledBodyLeftInner = styled(Box)({
    maxWidth: '232px',
    width: '100%',
})
const StyledBodyLeftInnerTitle = styled(Box)({
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#9b9b9b',
    margin: 0,
})
const StyledBodyLeftInnerResult = styled(Box)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 500,
    color: theme?.palette?.common?.black,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
}))

export interface ComputerSessionRequestDetailsProps {
    item: any
    open: boolean
    onClose: () => void
    title: string
    canCheckIn: boolean
    handleCheckIn: () => void
    handleConnect: () => void
    handleMasterUnlock: () => void
    masterPassword: any
    parent: any
    showStatus: boolean
}

export const ComputerSessionRequestDetails: FC<
    ComputerSessionRequestDetailsProps
> = (props) => {
    const {
        open,
        onClose,
        title,
        item,
        canCheckIn,
        handleMasterUnlock,
        masterPassword,
        showStatus,
    } = props
    const { t } = useTranslation()
    const theme = useTheme()
    const { getMyTasksUrl } = useUrlGenerator(useAllApplications)
    const getTimeValues = (startTimeUtc: any, endTimeUtc: any) => {
        if (startTimeUtc && endTimeUtc) {
            const timeDifference = moment(endTimeUtc).diff(
                moment(startTimeUtc),
                'minutes',
            )
            const h = Math.floor(timeDifference / 60)
            const m = Math.floor(timeDifference % 60)
            const hDisplay = h > 0 ? h + (h === 1 ? ' hour ' : ' hours ') : ''
            const mDisplay =
                m > 0 ? m + (m === 1 ? ' minute ' : ' minutes ') : ''
            return hDisplay + mDisplay
        }
        return '---'
    }
    return (
        <Dialog
            scroll={'body'}
            open={open}
            onClose={onClose}
            aria-labelledby={title}
            aria-describedby={title}
            PaperProps={{
                sx: {
                    maxWidth: '1000px',
                    width: '840px',
                    [`@media (max-width:${mobileScreenWidth})`]: {
                        margin: '64px 16px',
                        width: 'calc(100% - 32px) !important',
                        maxWidth: 'calc(100% - 32px) !important ',
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    height: '80px',
                    alignItems: 'center',
                    padding: '16px 12px 16px 32px',
                    backgroundColor: theme?.palette?.common?.white,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#3b454d',
                    }}
                >
                    {title ? title : '---'}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                    }}
                >
                    <IconButton onClick={onClose} size="large">
                        <Icon name="Close" color="#959598" />
                    </IconButton>
                </Box>
            </Box>
            <StyledSectionHeader>
                {t('Common_SessionRequestDetails')}
            </StyledSectionHeader>
            <Box
                sx={{
                    backgroundColor: '#fbfbfd',
                    padding: '24px 40px',
                    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
                    '&:focus': {
                        outline: 'none',
                    },
                }}
            >
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_Credentials')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.credentialFriendlyName
                                ? item.credentialFriendlyName
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('CheckedOutBy')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.requestorFriendlyName
                                ? item.requestorFriendlyName
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_WhoApproved')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.approverFriendlyName
                                ? item.approverFriendlyName
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_StartTime')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.startTimeUtc
                                ? moment
                                      .utc(item.startTimeUtc)
                                      .local()
                                      .format('L LT')
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_EndTime')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.endTimeUtc
                                ? moment
                                      .utc(item.endTimeUtc)
                                      .local()
                                      .format('L LT')
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_BusinessRequest')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult
                            as="h2"
                            sx={{
                                ...(item?.businessRequestId &&
                                item.businessRequestName
                                    ? {
                                          '& a': {
                                              color: `${theme?.palette?.primary?.main} !important`,
                                              textDecoration: 'none !important',
                                          },
                                      }
                                    : {}),
                            }}
                        >
                            {item?.businessRequestId &&
                            item.businessRequestName ? (
                                <a
                                    rel="noreferrer"
                                    target="_blank"
                                    href={getMyTasksUrl(
                                        `/myRequests/businessRequests?id=${item.businessRequestId}`,
                                    )}
                                >
                                    {item.businessRequestName
                                        ? item.businessRequestName
                                        : '---'}
                                </a>
                            ) : item.businessRequestName ? (
                                item.businessRequestName
                            ) : (
                                '---'
                            )}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_BusinessRequestItem')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult
                            as="h2"
                            sx={{
                                ...(item?.businessRequestId &&
                                item.businessRequestName
                                    ? {
                                          '& a': {
                                              color: `${theme?.palette?.primary?.main} !important`,
                                              textDecoration: 'none !important',
                                          },
                                      }
                                    : {}),
                            }}
                        >
                            {item?.businessRequestItemId &&
                            item.businessRequestItemName ? (
                                <a
                                    rel="noreferrer"
                                    target="_blank"
                                    href={getMyTasksUrl(
                                        `/myRequests/businessRequestItems?id=${item.businessRequestItemId}`,
                                    )}
                                >
                                    {item.businessRequestItemName
                                        ? item.businessRequestItemName
                                        : '---'}
                                </a>
                            ) : item.businessRequestItemName ? (
                                item.businessRequestItemName
                            ) : (
                                '---'
                            )}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_ActualCheckOutTime')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.userCheckedOutTimeUtc
                                ? moment
                                      .utc(item.userCheckedOutTimeUtc)
                                      .local()
                                      .format('L LT')
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_ActualCheckInTime')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.userCheckedInTimeUtc
                                ? moment
                                      .utc(item.userCheckedInTimeUtc)
                                      .local()
                                      .format('L LT')
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_UserAccount')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.credentialFriendlyName
                                ? item.credentialFriendlyName
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_TimeRequested')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {getTimeValues(item.startTimeUtc, item.endTimeUtc)}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_TimeUsed')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {getTimeValues(
                                item.userCheckedOutTimeUtc,
                                item.userCheckedInTimeUtc,
                            )}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_CheckoutBy')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.requestorFriendlyName
                                ? item.requestorFriendlyName
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_CheckinBy')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyRightInnerResult as="h2">
                            {item.checkedInByPersonName
                                ? item.checkedInByPersonName
                                : '---'}
                        </StyledBodyRightInnerResult>
                    </Box>
                </StyledBodyInner>
            </Box>
            {showStatus && (
                <Box>
                    <MasterLockOverlay
                        btnLabel={t('Common_Unlock')}
                        showMasterLock={!masterPassword}
                        handleMasterUnlock={() => handleMasterUnlock()}
                    >
                        <Box display="flex" width="100%" padding="24px 32px">
                            <Box minWidth="167px">
                                <StyledTabConnectButton>
                                    <ComputerConnectBtn
                                        parentStyle={{}}
                                        showPrefixIcon={true}
                                        data={item}
                                        drawerView={false}
                                        showCalenderIcon={false}
                                    />
                                </StyledTabConnectButton>
                            </Box>
                        </Box>
                    </MasterLockOverlay>
                </Box>
            )}
            {canCheckIn && (
                <Box>
                    <MasterLockOverlay
                        btnLabel={t('Common_Unlock')}
                        showMasterLock={!masterPassword}
                        handleMasterUnlock={() => handleMasterUnlock()}
                    >
                        <Box display="flex" width="100%" padding="24px 32px">
                            <Box minWidth="167px">
                                <StyledTabConnectButton>
                                    <ComputersCheckInButton
                                        data={{ resource: item }}
                                        labelStyleProp={{ fontSize: '14px' }}
                                        buttonStyleProp={{ width: '145px' }}
                                        showIcon={true}
                                    />
                                </StyledTabConnectButton>
                            </Box>
                        </Box>
                    </MasterLockOverlay>
                </Box>
            )}
        </Dialog>
    )
}

export default ComputerSessionRequestDetails
